import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { CreateExportDocument, FetchEmployeesSettingsDocument } from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import { LeaveTypeSelectorInput } from 'modules/core/components/Inputs/LeaveTypeSelectorInput'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import companies from 'modules/core/store/collections/companies'
import { companyHasBrazilianLegalEntity } from 'modules/core/store/collections/legalEntities'

import { transformFiltersToEmployeeFiltersInput } from 'modules/attendance/components/TimeTrackingCollection/lib/transformFilters'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimeTrackingData } from 'modules/attendance/lib/timeTrackingDataContext'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

import OrdinanceAlert from './OrdinanceAlert'
import {
  FILE_FORMATS,
  Option,
  TIME_FORMATS,
  TIME_PERIODS,
  TYPES,
  fileFormatAvailableOptions,
  timeFormatOptions,
  timePeriodOptions,
  useTypeAvailableOptions,
} from './options'

class ReportsForm extends Form {
  constructor(values: Record<string, string | number | undefined | number[] | undefined[]>) {
    super(values, {
      type: 'string',
      file_format: 'string',
      time_format: 'string',
      time_period: 'string',
      leave_type_ids: 'any',
      start_on: 'date',
      end_on: 'date',
    })
  }
}

interface Props {
  onClose: () => void
  timeoffEnabled: boolean
  periodType: string
  canBrazilianExportAccess: boolean
}

const calculateStartOn = (form: ReportsForm): moment.Moment => {
  return stringToDate(form.get('start_on').value, 'ui')
}

const calculateEndOn = (form: ReportsForm): moment.Moment => {
  return stringToDate(form.get('end_on').value, 'ui')
}

const showTimeFormat = (form: ReportsForm): boolean => {
  const { value: type } = form.get('type')
  const { value: fileFormat } = form.get('file_format')

  switch (fileFormat) {
    case FILE_FORMATS.txt:
      return false
    case FILE_FORMATS.pdf:
      if (
        [TYPES.full_version, TYPES.full_version_summary, TYPES.workplaces_geolocation].includes(
          type
        )
      )
        return true
      return false
    case FILE_FORMATS.excel:
      if (
        [
          TYPES.default_export,
          TYPES.full_version,
          TYPES.full_version_summary,
          TYPES.workplaces_geolocation,
          TYPES.pivot_table,
        ].includes(type)
      )
        return true
      return false
    default:
      return false
  }
}

const showTimePeriod = (form: ReportsForm): boolean => {
  const { value: type } = form.get('type')

  const allowedTypes = [TYPES.default_export]

  return allowedTypes.includes(type)
}

const Exports = ({ onClose, timeoffEnabled, periodType, canBrazilianExportAccess }: Props) => {
  const {
    selectAll: contextSelectAll,
    selectedEmployeeIds: contextSelectedEmployeeIds,
    excludedEmployeeIds: contextExcludedEmployeeIds,
    filters: contextFilters,
    search: contextSearch,
    selectedEmployeeCount: contextSelectedEmployeeCount,
    totalEmployeeCount,
  } = useTimeTrackingData()
  useAsyncData(async () => {
    await leaveTypes.fetch()
  }, [])
  const visibleLeaveTypes = leaveTypes.active

  const defaultType = TYPES.full_version
  const defaultFormatType = FILE_FORMATS.excel

  const { stringDateFrom, stringDateTo } = useAttendanceDateNavigator()
  const defaultTimePeriod = periodType === 'weekly' ? TIME_PERIODS.week : TIME_PERIODS.month

  const company = companies.current
  const { access, employee } = useCurrentSession()

  const { data: employeesSettings } = useQuery(FetchEmployeesSettingsDocument, {
    variables: {
      startOn: stringDateFrom,
      endOn: stringDateTo,
    },
  })

  const [leaveTypeIds, setSelectedLeaveTypes] = useState<number[]>([])

  const hasGeoLocationExport = EmployeesSettingsUtils.hasGeoLocation(
    employeesSettings?.attendance?.employeesSettingsConnection?.nodes ?? []
  )

  const form = useLazyRef(
    () =>
      new ReportsForm({
        type: defaultType,
        leaveTypeIds: [],
        time_format: TIME_FORMATS.hours_and_minutes,
        time_period: defaultTimePeriod,
        file_format: defaultFormatType,
        start_on: stringDateFrom,
        end_on: stringDateTo,
      })
  ).current

  const noEmployees = (): boolean => {
    return totalEmployeeCount === 0
  }

  const noEmployeesSelected = (): boolean => {
    return contextSelectedEmployeeCount === 0
  }

  const { type, time_format, time_period, file_format, start_on, end_on } = form.data()

  const trackEvent = () => {
    const startDate = stringToDate(form.get('start_on').value, 'ui')
    const endDate = stringToDate(form.get('end_on').value, 'ui')

    const properties: TrackingProperties = {
      export_type: type,
      export_format: file_format,
      time_format: time_format,
      employees_select: contextSelectAll ? 'all' : contextSelectedEmployeeIds,
      date_from: start_on,
      date_to: end_on,
      range_number_of_days: endDate.diff(startDate, 'days') + 1, // include end date as well,
    }

    if (contextSelectedEmployeeCount > 0)
      properties.number_of_employees = contextSelectedEmployeeCount
    if (leaveTypeIds.length > 0) properties.number_of_absence_types = leaveTypeIds.length

    track(trackingEvents.timeTracking.exportTimeTrackingSubmit, properties)
  }

  const [createExport] = useMutation(CreateExportDocument, {
    extractorFn: (data) => data.attendanceMutations.createAttendanceExport,
  })

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const filtersPayload =
        contextFilters != null
          ? transformFiltersToEmployeeFiltersInput(contextFilters, contextSearch)
          : null

      await createExport({
        variables: {
          startOn: i18nDateToString(calculateStartOn(form), 'backend'),
          endOn: i18nDateToString(calculateEndOn(form), 'backend'),
          employeeIds: contextSelectAll ? [] : contextSelectedEmployeeIds,
          leaveTypeIds: leaveTypeIds,
          timeFormat: time_format,
          timePeriod: time_period,
          type: type,
          fileFormat: file_format,
          selectAll: contextSelectAll,
          excludedEmployeeIds: contextExcludedEmployeeIds,
          filters: filtersPayload,
        },
      })

      closePortal()
      trackEvent()
      dispatch({
        type: EventTypes.TIMETRACKING_REPORT_DOWNLOADED,
      })
    },
    {
      successFlash: true,
      successMessage: i18n.t('clock_in.modals.export.notice_text'),
    }
  )

  const onSelectLeaveTypes = (ids: number[]) => {
    setSelectedLeaveTypes(ids)
  }

  const checkBrazilianLegalEntity = (): boolean => {
    if (canBrazilianExportAccess) {
      return companyHasBrazilianLegalEntity(company)
    }

    if (employee?.legalEntity) {
      return employee.legalEntity.country === 'br'
    }

    return false
  }
  const hasBrazilianLegalEntity = checkBrazilianLegalEntity()

  const exportTypeOptions = useTypeAvailableOptions({
    hasBrazilianLegalEntity,
    hasGeoLocationExport,
  })
  const fileFormatOptions = useCallback(
    () => fileFormatAvailableOptions(form.get('type').value),
    [form, form.get('type').value]
  )

  const showLeaveSelector = (): boolean => {
    if (!timeoffEnabled) {
      return false
    }

    return (
      (form.get('type').value === TYPES.default_export &&
        form.get('file_format').value === 'excel') ||
      [TYPES.full_version, TYPES.pivot_table].includes(form.get('type').value)
    )
  }

  const setFormatValue = (options: Option[]) => {
    form.get('file_format').set(options[0]?.value)
  }

  const forfaitjourEnabled = (): boolean => hasFeature('dev_forfaitjour_in_time_management')

  return (
    <Modal
      illustration="clock-app"
      title={i18n.t('time_tracking.modals.export.title')}
      description={i18n.t('time_tracking.modals.export.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      disableFocusTrap
      footer={
        <Button disabled={noEmployees() || noEmployeesSelected() || submitting} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      {hasBrazilianLegalEntity && (
        <FormRow>
          <OrdinanceAlert />
        </FormRow>
      )}
      <FormRow>
        <Select
          label={i18n.t('time_tracking.modals.export.options.export_type')}
          field={form.get('type')}
          options={exportTypeOptions}
          onChange={() => setFormatValue(fileFormatOptions())}
        />
      </FormRow>
      <FormRow>
        <Select
          label={i18n.t('time_tracking.modals.export.options.file_format')}
          field={form.get('file_format')}
          options={fileFormatOptions()}
          readonly={fileFormatOptions().length === 1}
        />
      </FormRow>
      {showTimeFormat(form) && (
        <FormRow>
          <Select
            label={i18n.t('time_tracking.modals.export.options.time_format_label')}
            description={
              forfaitjourEnabled()
                ? i18n.t('time_tracking.modals.export.options.forfait_jours_disclaimer')
                : undefined
            }
            field={form.get('time_format')}
            options={timeFormatOptions()}
          />
        </FormRow>
      )}
      {showTimePeriod(form) && (
        <FormRow>
          <Select
            label={i18n.t('clock_in.modals.export.options.time_period')}
            field={form.get('time_period')}
            options={timePeriodOptions()}
          />
        </FormRow>
      )}
      <FormRow>
        <Box paddingBottom="s2">
          <Text size="200" weight="medium">
            {i18n.t('time_tracking.modals.export.employees')}
          </Text>
        </Box>
        <Box
          padding="s16"
          background="grey100"
          border={{ all: { width: 's2', style: 'solid', color: 'grey300' } }}
          borderRadius={{ all: 'abs008' }}
        >
          {contextSelectedEmployeeCount === 0 ? (
            <Text size="300">{i18n.t('time_tracking.modals.export.no_employees_selected')}</Text>
          ) : (
            <Text size="300">
              {i18n.tp('time_tracking.modals.export.employees_selected', {
                count: contextSelectedEmployeeCount,
              })}
            </Text>
          )}

          <Box paddingTop="s2">
            <Text size="200">{i18n.t('time_tracking.modals.export.selection_can_be_changed')}</Text>
          </Box>
        </Box>
      </FormRow>

      {showLeaveSelector() && (
        <FormRow>
          <LeaveTypeSelectorInput
            label={i18n.t('time_tracking.modals.export.absence_types')}
            attachment="middle center"
            leaveTypes={visibleLeaveTypes}
            selectedIds={leaveTypeIds}
            onSubmit={onSelectLeaveTypes}
            showCreateLeave={visibleLeaveTypes.length === 0}
          />
        </FormRow>
      )}
      <FormRow multi>
        <DayPicker field={form.get('start_on')} label={i18n.t('common.from')} />
        <DayPicker
          field={form.get('end_on')}
          label={i18n.t('common.to')}
          minDate={stringToDate(form.get('start_on').value, 'ui')}
        />
      </FormRow>
    </Modal>
  )
}

export default observer(Exports)
